import React from 'react';
import { SubscriptionTier } from '../types';
import HelpBubble from './HelpBubble';

interface TrialExpiredModalProps {
  onUpgrade: () => void;
  onLogout?: () => void; // Optional logout function
  previousTier?: SubscriptionTier; // The tier before it expired (trial, e_rank, s_rank)
}

const TrialExpiredModal: React.FC<TrialExpiredModalProps> = ({ onUpgrade, onLogout, previousTier = 'trial' }) => {
  // Determine messaging based on what expired
  const wasPaidSubscription = previousTier === 'e_rank' || previousTier === 's_rank';
  
  const title = wasPaidSubscription 
    ? 'SUBSCRIPTION EXPIRED'
    : 'YOUR TRIAL HAS ENDED';
  
  const subtitle = wasPaidSubscription
    ? 'Renew your subscription to continue'
    : 'Continue your journey to greatness';
  
  const warningMessage = wasPaidSubscription
    ? 'Your subscription has ended. This may be due to a payment issue or cancellation.'
    : 'Your 14-day free trial has ended.';

  return (
    <div className="fixed inset-0 bg-black/95 flex items-center justify-center z-[99999] p-4 animate-fade-in">
      <div className="system-window max-w-2xl w-full">
        {/* Animated Header */}
        <div className="text-center mb-8">
          <div className="text-6xl mb-4 animate-float">
            {wasPaidSubscription ? '💳' : '⚔️'}
          </div>
          <h2 className="text-3xl sm:text-4xl font-black font-orbitron glow-text mb-2">
            {title}
          </h2>
          <p className="text-lg text-gray-400 font-rajdhani">
            {subtitle}
          </p>
        </div>

        {/* Warning Message */}
        {wasPaidSubscription && (
          <div className="mb-6 p-4 bg-red-950/40 border-2 border-red-500/50 rounded relative overflow-hidden">
            <div className="absolute top-0 left-0 w-1 h-full bg-red-500"></div>
            <div className="flex items-start gap-3">
              <span className="text-red-500 text-2xl flex-shrink-0">⚠️</span>
              <div>
                <p className="font-orbitron font-bold text-red-400 text-sm mb-1">PAYMENT ISSUE</p>
                <p className="text-red-200 text-xs font-rajdhani">
                  {warningMessage} Please update your payment method or resubscribe to restore access to all features.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Benefits */}
        <div className="bg-system-darker border-2 border-system-blue/30 rounded-lg p-6 mb-6">
          <h3 className="font-orbitron font-bold text-system-blue mb-4 text-center">
            ✨ WHAT YOU'LL KEEP WITH A SUBSCRIPTION
          </h3>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex items-start gap-3">
              <span className="text-2xl">🤖</span>
              <div>
                <p className="font-bold text-white">AI-Powered Quests</p>
                <p className="text-sm text-gray-400">Let AI plan your day</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <span className="text-2xl">🗺️</span>
              <div>
                <p className="font-bold text-white">Smart Roadmaps</p>
                <p className="text-sm text-gray-400">Achieve any goal</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <span className="text-2xl">⚡</span>
              <div>
                <p className="font-bold text-white">Passive Skills</p>
                <p className="text-sm text-gray-400">Build powerful habits</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <span className="text-2xl">📊</span>
              <div>
                <p className="font-bold text-white">Your Progress</p>
                <p className="text-sm text-gray-400">All your data saved</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <span className="text-2xl">🎨</span>
              <div>
                <p className="font-bold text-white">Premium Themes</p>
                <p className="text-sm text-gray-400">Customize your experience</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <span className="text-2xl">📅</span>
              <div>
                <p className="font-bold text-white">Full Calendar</p>
                <p className="text-sm text-gray-400">Plan your entire life</p>
              </div>
            </div>
          </div>
        </div>

        {/* Pricing Preview */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          <div className="border-2 border-system-blue/50 rounded-lg p-4 text-center">
            <p className="text-sm text-gray-400 mb-1">E-Rank Hunter</p>
            <div className="flex items-baseline justify-center gap-2">
              <span className="text-3xl font-black font-orbitron text-white">$2.99</span>
              <span className="text-sm text-gray-400 line-through">$5.99</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">50 AI quests/month</p>
          </div>
          
          <div className="border-2 border-system-purple rounded-lg p-4 text-center bg-system-purple/10">
            <p className="text-sm text-system-purple mb-1 font-bold">S-Rank Hunter ⭐</p>
            <div className="flex items-baseline justify-center gap-2">
              <span className="text-3xl font-black font-orbitron text-white">$4.99</span>
              <span className="text-sm text-gray-400 line-through">$9.99</span>
            </div>
            <p className="text-xs text-system-purple mt-1 font-bold">UNLIMITED AI</p>
          </div>
        </div>

        {/* CTA Button */}
        <button
          onClick={onUpgrade}
          className="w-full bg-gradient-to-r from-system-blue to-system-purple text-white py-4 rounded font-orbitron font-bold text-lg tracking-wider transition-all hover:scale-105 hover:shadow-neon-purple mb-4"
        >
          {wasPaidSubscription 
            ? '💳 RENEW SUBSCRIPTION & RESTORE ACCESS'
            : '⚔️ UPGRADE NOW & CONTINUE YOUR JOURNEY'
          }
        </button>

        {/* Logout Button */}
        {onLogout && (
          <button
            onClick={onLogout}
            className="w-full bg-gray-700 hover:bg-gray-600 text-gray-300 py-3 rounded font-orbitron font-bold text-sm tracking-wider transition-all hover:scale-105 mb-4"
          >
            🚪 LOGOUT FROM ACCOUNT
          </button>
        )}

        {/* Guarantee */}
        <div className="text-center text-sm text-gray-400">
          <p>✅ Cancel anytime • 💳 Secure payment • 🔒 Your data is safe</p>
        </div>
      </div>

      {/* Help Bubble - Always accessible during trial expiration */}
      <HelpBubble />
    </div>
  );
};

export default TrialExpiredModal;

